import {
  createPathParamCRUD,
  getPathParam,
  createPathParam,
  getPaginatedFilteredPathParam,
  type NoPathParams,
} from "@/api/shared/crud";
import { createService } from "@/api/shared/service";
import { formatDate } from "@/utils/Formatters";
import type { TItemProps } from "@/types/input";
import type { TEmail } from "@/api/shared/contacts";
import type {
  TPatient,
  TPatientInsurance,
  TPatientSearchFilters,
  TPatientConsentTypes,
  TAllAvailablePractices,
  TPatientTreatmentPlan,
  TPatientPublicRegistrationResponse,
} from "./types";

export * from "./types";

/**
 * The patient's active treatment plan — the first entry of `actual_treatment_plans`.
 * Use this instead of reaching for `patient.actual_treatment_plans?.[0]` directly.
 */
export const getActualTreatmentPlan = (
  patient?: Pick<TPatient, "actual_treatment_plans"> | null
): TPatientTreatmentPlan | undefined => patient?.actual_treatment_plans?.[0] ?? undefined;

/**
 * The id of the patient's active treatment plan, if any.
 */
export const getActualTreatmentPlanId = (
  patient?: Pick<TPatient, "actual_treatment_plans"> | null
): string | undefined => getActualTreatmentPlan(patient)?.id;

export const TPatientEmailDefault: TEmail = {
  email: "",
};

export const TPatientInsuranceDefault: TPatientInsurance = {
  insurance_company: null,
  insurance_company_data: null,
  number: "",
  start: null,
  end: null,
};

export const patientSelectable = (patient: TPatient): TItemProps => {
  const email = patient.emails?.find((e) => e.primary)?.email;
  const phone = patient.phones?.length
    ? `(${patient.phones[0]?.prefix}) ${patient.phones[0]?.number}`
    : "";
  return {
    title: [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(" "),
    subtitle: [patient.birth_date ? `(${formatDate(patient.birth_date)})` : "", phone, email]
      .filter(Boolean)
      .join(" | "),
    value: patient.id,
  };
};

const SLUG = "patient";

export default createService(SLUG, {
  ...createPathParamCRUD<
    TPatient,
    NoPathParams,
    TPatientSearchFilters,
    TPatient,
    Partial<TPatient>
  >(SLUG),

  // Sort/ordering fields are declared per-column via the table headers' `sortKey`,
  // so no service-level ordering `fieldMap` is needed here.
  getPaginatedWithReferral: getPaginatedFilteredPathParam<
    TPatient,
    NoPathParams,
    TPatientSearchFilters
  >(`${SLUG}/with-referral`),

  createPublicPatient: createPathParam<
    TPatientPublicRegistrationResponse,
    NoPathParams,
    Partial<TPatient>
  >(`${SLUG}/public/`),

  validate: createPathParam<TPatient, { id: string }, Partial<TPatient>>(`${SLUG}/{id}/confirm/`),

  sendDmLoginLink: createPathParam<unknown, { id: string }, Record<string, never>>(
    `${SLUG}/{id}/send_dm_login_link/`
  ),

  listOfAvailableTreatmentPlans: getPathParam<string[], { patientId: string }>(
    `${SLUG}/{patientId}/list-of-available-tratments/`
  ),

  listOfAvailableTreatmentForStaff: getPathParam<string[]>(`${SLUG}/allowed-treatment-for-staff/`),

  listOfAvailablePracticesForTreatmentType: getPathParam<TAllAvailablePractices>(
    `${SLUG}/allowed-practice-for-treatment-type/`
  ),

  emails: {},

  phones: {},

  consentTypes: createService("patient/consents", {
    ...createPathParamCRUD<TPatientConsentTypes>("patient/consents"),

    getAllPublic: getPathParam<TPatientConsentTypes[]>(`patient/public/consents/`),
  }),
});
